import React from "react";
import type {
  SimpleTableConfig,
  BaseEntity,
  EntityHooks,
  FieldValue,
  SerializedViewState,
} from "../types";
import type { AuthenticatedUser } from "@/lib/types/unified";
import type { QuickFilterState } from "@/components/shared/DataTable-modular/types";
import { validateInlineEdit } from "@/lib/hooks/api/validation";
import { toast } from "sonner";

/**
 * Props for useSimpleTableCallbacks hook
 */
export interface UseSimpleTableCallbacksProps<TData extends BaseEntity> {
  config: SimpleTableConfig<TData>;
  data: TData[];
  state: ReturnType<typeof import("./useSimpleTableState").useSimpleTableState<TData>>;
  hooks: EntityHooks<TData>;
  appUser?: AuthenticatedUser;
  onInlineEdit?: (payload: Record<string, FieldValue>) => Promise<void>;
  onBulkAction?: (actionId: string, selectedItems: TData[]) => void;
  onSavedViewsReady?: (
    serializeFilters: () => SerializedViewState,
    applyFilters: (filters: SerializedViewState) => void
  ) => void;
  contextVisibleColumns?: string[];
  contextToggleColumn?: (columnId: string) => void;
  quickFilterState?: QuickFilterState;
  setQuickFilterState?: (state: QuickFilterState) => void;
}

/**
 * Hook that creates validated callbacks for SimpleTable operations
 * Wraps onInlineEdit with validation to ensure type safety
 */
export function useSimpleTableCallbacks<TData extends BaseEntity>({
  config,
  data,
  state,
  hooks,
  appUser,
  onInlineEdit,
  onBulkAction,
  onSavedViewsReady,
  contextVisibleColumns,
  contextToggleColumn,
  quickFilterState,
  setQuickFilterState,
}: UseSimpleTableCallbacksProps<TData>) {
  /**
   * Create a validated inline edit handler that validates field changes
   */
  const createValidatedInlineEditHandler = React.useCallback(
    (rowData: TData) => {
      return async (fieldId: string, newValue: FieldValue) => {
        if (!onInlineEdit) return;

        try {
          // Get field config
          const fieldConfig = config.fields[fieldId];
          if (!fieldConfig) {
            toast.error(`Field "${fieldId}" not found in configuration`);
            return;
          }

          // Validate the inline edit
          const validation = validateInlineEdit(fieldId, newValue, fieldConfig);

          if (!validation.isValid) {
            const errorMessage = validation.errors.join("; ");
            toast.error(`Validation failed: ${errorMessage}`);
            return;
          }

          // Call the mutation with validated data
          const updatedItem = {
            ...rowData,
            [fieldId]: validation.sanitizedValue,
          };

          await onInlineEdit(updatedItem);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Failed to update field";
          toast.error(errorMessage);
          console.error("Inline edit error:", error);
        }
      };
    },
    [config.fields, onInlineEdit]
  );

  /**
   * Create handle select all
   */
  const createHandleSelectAll = React.useCallback(() => {
    return (selected: boolean, paginatedData: TData[]) => {
      // Implementation would go here
    };
  }, []);

  /**
   * Create handle bulk action
   */
  const createHandleBulkAction = React.useCallback(() => {
    return (actionId: string, paginatedData: TData[]) => {
      if (onBulkAction) {
        onBulkAction(actionId, paginatedData);
      }
    };
  }, [onBulkAction]);

  /**
   * Handle body scroll
   */
  const handleBodyScroll = React.useCallback(
    (tableBodyRef: React.RefObject<HTMLDivElement>, tableHeaderScrollRef: React.RefObject<HTMLDivElement>) => {
      if (tableBodyRef.current && tableHeaderScrollRef.current) {
        tableHeaderScrollRef.current.scrollLeft = tableBodyRef.current.scrollLeft;
      }
    },
    []
  );

  /**
   * Handle header scroll
   */
  const handleHeaderScroll = React.useCallback(
    (tableBodyRef: React.RefObject<HTMLDivElement>, tableHeaderScrollRef: React.RefObject<HTMLDivElement>) => {
      if (tableBodyRef.current && tableHeaderScrollRef.current) {
        tableBodyRef.current.scrollLeft = tableHeaderScrollRef.current.scrollLeft;
      }
    },
    []
  );

  return {
    createValidatedInlineEditHandler,
    createHandleSelectAll,
    createHandleBulkAction,
    handleBodyScroll,
    handleHeaderScroll,
  };
}
